<template>
	<v-form class="plan-form" @submit.prevent="submitForm">
		<div class="plan-form-shell">
			<header class="plan-form-intro">
				<span class="plan-form-intro-icon"><v-icon>assignment</v-icon></span>
				<div>
					<small>{{ idPlan ? 'EDICIÓN DEL PLAN' : 'NUEVO PLAN' }}</small>
					<h2>{{ idPlan ? 'Actualiza la configuración del plan' : 'Configura un nuevo plan' }}</h2>
					<p>Define la información comercial, capacidad, servicios y vigencia.</p>
				</div>
			</header>

			<div class="plan-sections">
				<section class="plan-section plan-section-wide">
					<header class="plan-section-header">
						<span><v-icon>description</v-icon></span>
						<div><h3>Información general</h3><p>Identificación y descripción del plan.</p></div>
					</header>
					<div class="plan-fields plan-fields-general">
						<div class="plan-field">
							<v-text-field background-color="white" box hide-details label="Código"
								:readonly="!idPlan" v-model="$v.model.codePlan.$model"
								:error="$v.model.codePlan.$error" />
							<small v-if="!idPlan" class="plan-field-help">Generado automáticamente con fecha y hora.</small>
							<span class="form-group__message" v-if="!$v.model.codePlan.required">Código requerido</span>
						</div>
						<div class="plan-field">
							<v-text-field background-color="white" box hide-details label="Nombre del plan"
								v-model="$v.model.name.$model" :error="$v.model.name.$error" />
							<span class="form-group__message" v-if="!$v.model.name.required">Nombre del plan requerido</span>
						</div>
						<div class="plan-field plan-field-full">
							<v-textarea background-color="white" box hide-details height="82" label="Descripción"
								v-model="$v.model.description.$model" :error="$v.model.description.$error" />
							<span class="form-group__message" v-if="!$v.model.description.required">Descripción requerida</span>
						</div>
					</div>
				</section>

				<section class="plan-section plan-section-wide">
					<header class="plan-section-header">
						<span><v-icon>tune</v-icon></span>
						<div><h3>Capacidad y límites</h3><p>Pulsa “Activar límite” únicamente en las capacidades que incluirá el plan.</p></div>
					</header>
					<div class="plan-limit-grid">
						<article class="plan-limit-card plan-limit-card-featured" :class="{ 'plan-limit-card-disabled': !model.limitOptions.subsidiaries }">
							<div class="plan-limit-heading">
								<v-icon>business</v-icon><div><strong>Empresas</strong><small>Empresas administradas por el titular</small></div>
								<button type="button" class="plan-limit-toggle" :class="{ 'plan-limit-toggle-active': model.limitOptions.subsidiaries }" @click="toggleLimitOption('subsidiaries')"><span></span>{{ model.limitOptions.subsidiaries ? 'Activo' : 'Activar' }}</button>
							</div>
							<v-text-field v-if="model.limitOptions.subsidiaries" background-color="white" box hide-details label="Número de empresas" type="number" min="1"
								v-model="$v.model.subsidiariesLimit.$model" :error="$v.model.subsidiariesLimit.$error" />
							<button v-else type="button" class="plan-limit-enable" @click="toggleLimitOption('subsidiaries')"><v-icon>add_circle_outline</v-icon><span><strong>Activar límite</strong><small>Definir número de empresas</small></span></button>
							<span class="form-group__message" v-if="$v.model.subsidiariesLimit.$error && !$v.model.subsidiariesLimit.required">Cantidad requerida</span>
							<span class="form-group__message" v-if="$v.model.subsidiariesLimit.$error && !$v.model.subsidiariesLimit.minValue">Debe ser mayor o igual a uno</span>
						</article>
						<article class="plan-limit-card">
							<div class="plan-limit-heading"><v-icon>people</v-icon><div><strong>Accesos simultáneos</strong><small>Máximo de sesiones activas por empresa y aplicación</small></div></div>
							<v-text-field background-color="white" box hide-details label="Límite de accesos" type="number" min="1"
								v-model="$v.model.limitAccess.$model" :error="$v.model.limitAccess.$error" />
							<span class="form-group__message" v-if="!$v.model.limitAccess.required">Límite requerido</span>
							<span class="form-group__message" v-if="!$v.model.limitAccess.minValue">Debe ser mayor o igual a uno</span>
						</article>
						<article class="plan-limit-card" :class="{ 'plan-limit-card-disabled': !model.limitOptions.products }">
							<div class="plan-limit-heading">
								<v-icon>local_offer</v-icon><div><strong>Productos</strong><small>Productos registrados</small></div>
								<button type="button" class="plan-limit-toggle" :class="{ 'plan-limit-toggle-active': model.limitOptions.products }" @click="toggleLimitOption('products')"><span></span>{{ model.limitOptions.products ? 'Activo' : 'Activar' }}</button>
							</div>
							<v-text-field v-if="model.limitOptions.products" background-color="white" box hide-details label="Número de productos" type="number" min="1" v-model="$v.model.products.limitProducts.$model" :error="$v.model.products.limitProducts.$error" />
							<button v-else type="button" class="plan-limit-enable" @click="toggleLimitOption('products')"><v-icon>add_circle_outline</v-icon><span><strong>Activar límite</strong><small>Definir número de productos</small></span></button>
							<span class="form-group__message" v-if="$v.model.products.limitProducts.$error && !$v.model.products.limitProducts.required">Cantidad requerida</span>
							<span class="form-group__message" v-if="$v.model.products.limitProducts.$error && !$v.model.products.limitProducts.minValue">Debe ser mayor o igual a uno</span>
						</article>
						<article class="plan-limit-card" :class="{ 'plan-limit-card-disabled': !model.limitOptions.warehouses }">
							<div class="plan-limit-heading">
								<v-icon>store</v-icon><div><strong>Tiendas</strong><small>Establecimientos disponibles</small></div>
								<button type="button" class="plan-limit-toggle" :class="{ 'plan-limit-toggle-active': model.limitOptions.warehouses }" @click="toggleLimitOption('warehouses')"><span></span>{{ model.limitOptions.warehouses ? 'Activo' : 'Activar' }}</button>
							</div>
							<v-text-field v-if="model.limitOptions.warehouses" background-color="white" box hide-details label="Número de tiendas" type="number" min="1" v-model="$v.model.warehouses.limitWarehouses.$model" :error="$v.model.warehouses.limitWarehouses.$error" />
							<button v-else type="button" class="plan-limit-enable" @click="toggleLimitOption('warehouses')"><v-icon>add_circle_outline</v-icon><span><strong>Activar límite</strong><small>Definir número de tiendas</small></span></button>
							<span class="form-group__message" v-if="$v.model.warehouses.limitWarehouses.$error && !$v.model.warehouses.limitWarehouses.required">Cantidad requerida</span>
							<span class="form-group__message" v-if="$v.model.warehouses.limitWarehouses.$error && !$v.model.warehouses.limitWarehouses.minValue">Debe ser mayor o igual a uno</span>
						</article>
						<article class="plan-limit-card" :class="{ 'plan-limit-card-disabled': !model.limitOptions.orders }">
							<div class="plan-limit-heading">
								<v-icon>shopping_cart</v-icon><div><strong>Pedidos</strong><small>Pedidos permitidos</small></div>
								<button type="button" class="plan-limit-toggle" :class="{ 'plan-limit-toggle-active': model.limitOptions.orders }" @click="toggleLimitOption('orders')"><span></span>{{ model.limitOptions.orders ? 'Activo' : 'Activar' }}</button>
							</div>
							<v-text-field v-if="model.limitOptions.orders" background-color="white" box hide-details label="Número de pedidos" type="number" min="1" v-model="$v.model.orders.limitOrders.$model" :error="$v.model.orders.limitOrders.$error" />
							<button v-else type="button" class="plan-limit-enable" @click="toggleLimitOption('orders')"><v-icon>add_circle_outline</v-icon><span><strong>Activar límite</strong><small>Definir número de pedidos</small></span></button>
							<span class="form-group__message" v-if="$v.model.orders.limitOrders.$error && !$v.model.orders.limitOrders.required">Cantidad requerida</span>
							<span class="form-group__message" v-if="$v.model.orders.limitOrders.$error && !$v.model.orders.limitOrders.minValue">Debe ser mayor o igual a uno</span>
						</article>
					</div>

					<div class="plan-subsection">
						<div class="plan-roles-heading">
							<div class="plan-roles-title">
								<span><v-icon>group</v-icon></span>
								<div>
									<h4>Usuarios según perfil</h4>
									<p>Selecciona únicamente los perfiles incluidos en este plan y define su cantidad.</p>
								</div>
							</div>
							<div class="plan-roles-summary">
								<div><strong>{{ configuredProfiles }}</strong><small>seleccionados</small></div>
								<div><strong>{{ totalAssignedUsers }}</strong><small>usuarios</small></div>
								<button v-if="configuredProfiles" type="button" class="plan-roles-reset" @click="clearUserSelection">
									<v-icon>clear_all</v-icon> Quitar todos
								</button>
							</div>
						</div>

						<div class="plan-role-picker">
							<div class="plan-role-picker-toolbar">
								<label class="plan-role-search">
									<v-icon>search</v-icon>
									<input type="search" v-model.trim="roleSearch" placeholder="Buscar perfil por nombre o código..." />
									<button v-if="roleSearch" type="button" aria-label="Limpiar búsqueda" @click="roleSearch = ''"><v-icon>close</v-icon></button>
								</label>
								<span>{{ filteredUserProfiles.length }} disponibles</span>
							</div>
							<div v-if="filteredUserProfiles.length" class="plan-role-options">
								<button v-for="user in filteredUserProfiles" :key="user.code" type="button"
									class="plan-role-option" :class="{ 'plan-role-option-selected': user.isSelected }"
									@click="toggleUserProfile(user.code)">
									<span class="plan-role-option-check">
										<v-icon v-if="user.isSelected" class="plan-role-selected-icon">check</v-icon>
										<v-icon v-else>add</v-icon>
									</span>
									<span><strong>{{ user.name }}</strong><small>{{ user.code }}</small></span>
									<em>{{ user.isSelected ? 'Seleccionado' : 'Agregar' }}</em>
								</button>
							</div>
							<div v-else class="plan-role-search-empty"><v-icon>search_off</v-icon>No encontramos perfiles con esa búsqueda.</div>
						</div>

						<div v-if="selectedUserProfiles.length" class="plan-selected-roles">
							<div class="plan-selected-roles-heading">
								<div><strong>Perfiles incluidos</strong><small>Ajusta la cantidad permitida para cada perfil.</small></div>
								<span>{{ selectedUserProfiles.length }}</span>
							</div>
							<div class="plan-role-grid">
							<article class="plan-role-card" :class="{ 'plan-role-card-active': Number(user.quantity) > 0 }"
								v-for="user in selectedUserProfiles" :key="user.code">
								<div class="plan-role-information">
									<span class="plan-role-avatar">{{ getUserInitials(user.name) }}</span>
									<div>
										<strong>{{ user.name }}</strong>
										<small>{{ user.code }}</small>
									</div>
									<span class="plan-role-status">{{ Number(user.quantity) > 0 ? 'Incluido' : 'Sin cupos' }}</span>
								</div>
								<div class="plan-role-counter">
									<button type="button" :disabled="!Number(user.quantity)" :aria-label="`Disminuir ${user.name}`"
										@click="changeUserQuantity(user.formIndex, -1)"><v-icon>remove</v-icon></button>
									<label>
										<span>Cantidad</span>
										<input type="number" min="0" v-model="$v.model.users.$each[user.formIndex].quantity.$model" />
									</label>
									<button type="button" :aria-label="`Aumentar ${user.name}`"
										@click="changeUserQuantity(user.formIndex, 1)"><v-icon>add</v-icon></button>
								</div>
								<span class="form-group__message" v-if="!$v.model.users.$each[user.formIndex].quantity.minValue">Debe ser mayor o igual a cero</span>
							</article>
							</div>
						</div>
						<div v-else class="plan-roles-empty">
							<span><v-icon>person_add</v-icon></span>
							<div><strong>Aún no seleccionas perfiles</strong><p>Busca un perfil arriba y pulsa “Agregar”. Solo esos perfiles se enviarán con el plan.</p></div>
						</div>
					</div>
				</section>

				<section class="plan-section">
					<header class="plan-section-header">
						<span><v-icon>payment</v-icon></span>
						<div><h3>Precio y forma de pago</h3><p>Condiciones comerciales del plan.</p></div>
					</header>
					<div class="plan-fields">
						<div class="plan-field">
							<v-text-field background-color="white" box hide-details label="Precio" type="number" min="0"
								v-model="$v.model.price.$model" :error="$v.model.price.$error" />
							<span class="form-group__message" v-if="!$v.model.price.required">Precio requerido</span>
							<span class="form-group__message" v-if="!$v.model.price.minValue">Debe ser mayor o igual a cero</span>
						</div>
						<div class="plan-field">
							<v-select background-color="white" box hide-details item-text="name" item-value="code" label="Periodicidad"
								:items="paymentsForm" return-object v-model="$v.model.paymentFormId.$model"
								:error="$v.model.paymentFormId.$error" />
							<span class="form-group__message" v-if="$v.model.paymentFormId.$invalid">Periodicidad requerida</span>
						</div>
						<div class="plan-field">
							<v-text-field background-color="white" box hide-details label="Descuento" suffix="%" type="number" min="0" max="100"
								v-model="$v.model.promotions.discount.$model" :error="$v.model.promotions.discount.$error" />
							<span class="form-group__message" v-if="!$v.model.promotions.discount.required">Descuento requerido</span>
							<span class="form-group__message" v-if="!$v.model.promotions.discount.between">Debe estar entre 0 y 100</span>
						</div>
						<div class="plan-field">
							<v-text-field background-color="white" box hide-details label="Avisar antes del vencimiento" suffix="días" type="number" min="5"
								v-model="$v.model.notification.$model" :error="$v.model.notification.$error" />
							<span class="form-group__message" v-if="!$v.model.notification.required">Días de aviso requeridos</span>
							<span class="form-group__message" v-if="!$v.model.notification.minValue">Debe ser mayor o igual a cinco</span>
						</div>
						<div class="plan-field plan-field-full">
							<v-select multiple chips background-color="white" box hide-details item-text="name" item-value="code"
								label="Métodos de pago disponibles" :items="typePayments" v-model="model.typePayment" return-object />
						</div>
					</div>
				</section>

				<section class="plan-section" :class="{ 'plan-section-disabled': !model.electronicBillingEnabled }">
					<header class="plan-section-header">
						<span><v-icon>receipt</v-icon></span>
						<div><h3>Facturación electrónica</h3><p>Capacidad documental incluida.</p></div>
						<div class="plan-section-switch">
							<strong>{{ model.electronicBillingEnabled ? 'Activo' : 'Inactivo' }}</strong>
							<v-switch color="primary" hide-details v-model="model.electronicBillingEnabled" />
						</div>
					</header>
					<div v-if="model.electronicBillingEnabled" class="plan-fields">
						<div class="plan-field"><v-text-field background-color="white" box hide-details label="Facturas anuales" type="number" min="0" v-model="model.electronicBilling.quantity" /></div>
						<div class="plan-field"><v-text-field background-color="white" box hide-details label="Facturas mensuales" type="number" min="0" v-model="model.electronicBillingMonthly.quantity" /></div>
						<div class="plan-toggle plan-field-full">
							<div><strong>Firma electrónica</strong><small>Habilita la firma electrónica dentro del plan.</small></div>
							<v-switch color="primary" hide-details v-model="model.electronicFirm" />
						</div>
					</div>
					<div v-else class="plan-feature-disabled">
						<span><v-icon>receipt</v-icon></span>
						<div><strong>Facturación electrónica no incluida</strong><p>Activa el interruptor para configurar facturas y firma electrónica.</p></div>
					</div>
				</section>

				<section class="plan-section">
					<header class="plan-section-header">
						<span><v-icon>extension</v-icon></span>
						<div><h3>Otras funciones del plan</h3><p>Selecciona los servicios, integraciones y herramientas que estarán incluidos.</p></div>
					</header>
					<div class="plan-services-grid">
						<button v-for="service in model.additionalServices" :key="service.code" type="button"
							class="plan-service-card" :class="{ 'plan-service-card-active': service.isSelected }"
							:aria-pressed="service.isSelected" @click="toggleAdditionalService(service.code)">
							<span class="plan-service-icon"><v-icon>{{ service.icon }}</v-icon></span>
							<span class="plan-service-copy"><strong>{{ service.name }}</strong><small>{{ service.description }}</small></span>
							<span class="plan-service-action"><v-icon>{{ service.isSelected ? 'check' : 'add' }}</v-icon></span>
						</button>
						<button v-for="configuration in model.otherConfigurations" :key="configuration.code" type="button"
							class="plan-service-card" :class="{ 'plan-service-card-active': configuration.isSelected }"
							:aria-pressed="configuration.isSelected" @click="toggleOtherConfiguration(configuration.code)">
							<span class="plan-service-icon"><v-icon>{{ configuration.icon }}</v-icon></span>
							<span class="plan-service-copy"><strong>{{ configuration.name }}</strong><small>{{ configuration.description }}</small></span>
							<span class="plan-service-action"><v-icon>{{ configuration.isSelected ? 'check' : 'add' }}</v-icon></span>
						</button>
					</div>
					<div class="plan-fields plan-fields-kardex" v-if="kardexEnabled">
						<div class="plan-field plan-field-full">
							<v-text-field background-color="white" box hide-details min="0" type="number"
								label="Cantidad de recálculos Kardex" v-model="model.recalculateKardex" />
						</div>
					</div>
				</section>

				<section class="plan-section">
					<header class="plan-section-header">
						<span><v-icon>event</v-icon></span>
						<div><h3>Vigencia</h3><p>Periodo durante el cual estará disponible.</p></div>
					</header>
					<div class="plan-date-grid">
						<div><label>Fecha de inicio</label><date-component :date="model.startDate" :disabled-past-date="disabledDates" icon="/static/img/calendar.svg" @clear="clearDate('startDate')" @on-input="setDate({ model: 'startDate', value: $event })" /></div>
						<div><label>Fecha de finalización</label><date-component :date="model.endDate" :disabled-past-date="disabledDates" icon="/static/img/calendar.svg" @clear="clearDate('endDate')" @on-input="setDate({ model: 'endDate', value: $event })" /></div>
					</div>
				</section>
			</div>
		</div>

		<section class="modal-filter-footer">
			<modal-sidebar-button :is-loading="loading" :on-run-service="loading"
				success-text="Guardar plan" @accept-button-clicked="submitForm" @cancel-button-clicked="closeModal" />
		</section>
	</v-form>
</template>

<script>
import { format } from 'date-fns';
import { required, requiredIf, minValue, between } from 'vuelidate/lib/validators';
import dateComponent from '@/components/shared/date-component';
import { ADDITIONAL_SERVICES, toFeatureBoolean } from '@/shared/plan-features';

const PAYMENT_DURATION = {
	annual: 12,
	biannual: 6,
	quartely: 3,
	montly: 1,
};

function isConfigurationSelected(configurations, code) {
	const configuration = configurations.find(item => item.code === code);
	return Boolean(configuration && configuration.isSelected);
}

function optionalNumber(value) {
	return value === null || value === '' || typeof value === 'undefined' ? null : Number(value);
}

function positiveNumber(value) {
	const parsed = optionalNumber(value);
	return parsed && parsed > 0 ? parsed : null;
}

function limitMinValue(option) {
	return function validateLimit(value) {
		if (!this.model.limitOptions[option]) return true;
		const parsed = optionalNumber(value);
		return parsed === null || parsed >= 1;
	};
}

function formatPlanDate(value) {
	return value ? format(value, 'YYYY-MM-DD') : null;
}

function closeModal() {
	this.goTo('page-plan', { params: { projectId: this.projectId } });
}

function created() {
	const currentDate = new Date();
	const tomorrow = new Date(currentDate);
	tomorrow.setDate(tomorrow.getDate() + 1);
	if (!this.idPlan) this.model.codePlan = format(currentDate, 'YYYYMMDDHHmmss');
	this.model.startDate = format(currentDate, 'YYYY-MM-DD');
	this.model.endDate = format(tomorrow, 'YYYY-MM-DD');
	this.loadData();
}

async function loadRoles() {
	const params = { codeProject: this.$route.params.codeProject };
	try {
		const response = await this.$http.get('types-roles', { params });
		this.model.users = response.data.data.map(role => ({
			code: role.code.toLowerCase(),
			name: role.name,
			quantity: 0,
			isSelected: false,
		}));
	} catch (error) {
		this.showError();
	}
}

function applyPlanData(plan) {
	const promotions = plan.promotions || {};
	const promotionPaymentCode = this.paymentsForm
		.map(payment => payment.code)
		.find(code => Object.prototype.hasOwnProperty.call(promotions, code));
	const durationPayment = this.paymentsForm.find(
		payment => PAYMENT_DURATION[payment.code] === Number(plan.active_plan_time));
	const paymentCode = promotionPaymentCode || (durationPayment && durationPayment.code);
	const savedUsers = plan.users || {};
	const savedAdditionalServices = plan.additional_services || {};
	let savedTypePayments = plan.type_payment || [];
	if (!Array.isArray(savedTypePayments) && typeof savedTypePayments === 'object') {
		savedTypePayments = Object.keys(savedTypePayments)
			.filter(code => savedTypePayments[code]);
	}
	if (!Array.isArray(savedTypePayments)) savedTypePayments = [];
	this.model.codePlan = plan.code_plan;
	this.model.name = plan.name;
	this.model.description = plan.description;
	this.model.price = plan.price;
	this.model.limitAccess = plan.limit_access;
	this.model.paymentFormId = this.paymentsForm.find(item => item.code === paymentCode) || null;
	this.model.typePayment = savedTypePayments.map((payment) => {
		const code = typeof payment === 'string' ? payment : payment.code;
		return this.typePayments.find(typePayment => typePayment.code === code) || null;
	}).filter(Boolean);
	this.model.users = this.model.users.map(user => ({
		...user,
		isSelected: Object.prototype.hasOwnProperty.call(savedUsers, user.code),
		quantity: Object.prototype.hasOwnProperty.call(savedUsers, user.code) ?
			savedUsers[user.code] : 0,
	}));
	this.model.subsidiariesLimit = positiveNumber(plan.subsidiaries_limit);
	this.model.limitOptions.subsidiaries = Number(plan.subsidiaries_limit) > 0;
	this.model.electronicBilling.quantity = optionalNumber(plan.electronic_billing);
	this.model.electronicBillingMonthly.quantity = optionalNumber(plan.electronic_billing_monthly);
	this.model.electronicFirm = Boolean(Number(plan.electronic_firm));
	this.model.electronicBillingEnabled = Number(plan.electronic_billing) > 0 ||
		Number(plan.electronic_billing_monthly) > 0 || this.model.electronicFirm;
	this.model.products.limitProducts = positiveNumber(plan.products);
	this.model.warehouses.limitWarehouses = positiveNumber(plan.warehouses);
	this.model.orders.limitOrders = positiveNumber(plan.orders);
	this.model.limitOptions.products = Number(plan.products) > 0;
	this.model.limitOptions.warehouses = Number(plan.warehouses) > 0;
	this.model.limitOptions.orders = Number(plan.orders) > 0;
	this.model.promotions.discount = optionalNumber(promotions.discount);
	this.model.notification = plan.days_notification;
	this.model.recalculateKardex = Number(plan.recalculate_kardex) || 0;
	this.model.additionalServices = this.model.additionalServices.map(service => ({
		...service,
		isSelected: toFeatureBoolean(savedAdditionalServices[service.code]),
	}));
	this.model.otherConfigurations = this.model.otherConfigurations.map(configuration => ({
		...configuration,
		isSelected: toFeatureBoolean(plan[configuration.code]) ||
			(configuration.code === 'kardex' && toFeatureBoolean(plan.flag_kardex)),
	}));
	this.model.startDate = formatPlanDate(plan.start_date);
	this.model.endDate = formatPlanDate(plan.end_date);
}

async function loadData() {
	await this.loadRoles();
	if (!this.idPlan) return;
	try {
		const response = await this.$http.get(`plan/${this.idPlan}`);
		this.applyPlanData(response.data.data);
	} catch (error) {
		this.showError();
	}
}

function setDate({ model, value }) {
	this.model[model] = format(value, 'YYYY-MM-DD');
}

function clearDate(model) {
	this.model[model] = null;
}

function getUserInitials(name) {
	return (name || '')
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map(word => word.charAt(0))
		.join('')
		.toUpperCase();
}


function changeUserQuantity(index, adjustment) {
	const user = this.model.users[index];
	const currentQuantity = Number(user.quantity) || 0;
	this.$set(user, 'quantity', Math.max(0, currentQuantity + adjustment));
}

function toggleUserProfile(code) {
	const index = this.model.users.findIndex(user => user.code === code);
	if (index === -1) return;
	const user = this.model.users[index];
	const isSelected = !user.isSelected;
	this.$set(user, 'isSelected', isSelected);
	this.$set(user, 'quantity', isSelected ? (Number(user.quantity) || 1) : 0);
}

function clearUserSelection() {
	this.model.users = this.model.users.map(user => ({
		...user,
		isSelected: false,
		quantity: 0,
	}));
}

function toggleLimitOption(option) {
	this.model.limitOptions[option] = !this.model.limitOptions[option];
}

function toggleAdditionalService(code) {
	const service = this.model.additionalServices.find(item => item.code === code);
	if (service) this.$set(service, 'isSelected', !service.isSelected);
}

function toggleOtherConfiguration(code) {
	const configuration = this.model.otherConfigurations.find(item => item.code === code);
	if (configuration) this.$set(configuration, 'isSelected', !configuration.isSelected);
}

function buildUsersPayload(users) {
	return users.filter(user => user.isSelected).reduce((result, user) => ({
		...result,
		[user.code]: optionalNumber(user.quantity) || 0,
	}), {});
}

function buildPlanPayload() {
	const { model } = this;
	const paymentCode = model.paymentFormId.code;
	const payload = {
		start_date: model.startDate,
		end_date: model.endDate,
		code_plan: model.codePlan,
		name: model.name,
		description: model.description,
		price: Number(Number(model.price).toFixed(2)),
		promotions: {
			[paymentCode]: Number(model.promotions.discount),
			discount: Number(model.promotions.discount),
		},
		limit_access: Number(model.limitAccess),
		active_plan_time: PAYMENT_DURATION[paymentCode],
		users: buildUsersPayload(model.users),
		subsidiaries_limit: model.limitOptions.subsidiaries ? Number(model.subsidiariesLimit) : 0,
		products: model.limitOptions.products ? Number(model.products.limitProducts) : 0,
		electronic_billing: model.electronicBillingEnabled ?
			(optionalNumber(model.electronicBilling.quantity) || 0) : 0,
		electronic_billing_monthly: model.electronicBillingEnabled ?
			(optionalNumber(model.electronicBillingMonthly.quantity) || 0) : 0,
		electronic_firm: model.electronicBillingEnabled ? model.electronicFirm : false,
		warehouses: model.limitOptions.warehouses ? Number(model.warehouses.limitWarehouses) : 0,
		orders: model.limitOptions.orders ? Number(model.orders.limitOrders) : 0,
		days_notification: Number(model.notification),
		flag_kardex: isConfigurationSelected(model.otherConfigurations, 'kardex'),
		recalculate_kardex: isConfigurationSelected(model.otherConfigurations, 'kardex') ?
			Math.max(0, Number(model.recalculateKardex) || 0) : 0,
		type_payment: model.typePayment,
		additional_services: model.additionalServices.reduce((services, service) => ({
			...services,
			[service.code]: Boolean(service.isSelected),
		}), {}),
	};
	model.otherConfigurations.forEach((configuration) => {
		payload[configuration.code] = Boolean(configuration.isSelected);
	});
	return payload;
}

async function submitForm() {
	this.$v.$touch();
	if (this.$v.$invalid || this.loading) {
		if (this.$v.$invalid) {
			this.showError('Revisa los campos obligatorios o marcados en rojo antes de guardar.');
		}
		return;
	}
	this.loading = true;
	try {
		const url = this.idPlan ? `plan/edit/${this.idPlan}` : 'plan/create';
		const method = this.idPlan ? 'patch' : 'post';
		await this.$http[method](url, this.buildPlanPayload());
		this.showNotification(this.idPlan ? 'Registro actualizado correctamente' : 'Registro creado correctamente');
		this.closeModal();
	} catch (error) {
		this.showError();
	} finally {
		this.loading = false;
	}
}

function validations() {
	return {
		model: {
			name: { required },
			codePlan: { required },
			description: { required },
			price: { required, minValue: minValue(0) },
			promotions: { discount: { required, between: between(0, 100) } },
			notification: { required, minValue: minValue(5) },
			paymentFormId: { required },
			typePayment: { $each: { code: { required } } },
			limitAccess: { required, minValue: minValue(1) },
			users: { $each: { quantity: { minValue: minValue(0) } } },
			subsidiariesLimit: {
				required: requiredIf(function subsidiariesEnabled() {
					return this.model.limitOptions.subsidiaries;
				}),
				minValue: limitMinValue('subsidiaries'),
			},
			products: {
				limitProducts: {
					required: requiredIf(function productsEnabled() {
						return this.model.limitOptions.products;
					}),
					minValue: limitMinValue('products'),
				},
			},
			warehouses: {
				limitWarehouses: {
					required: requiredIf(function warehousesEnabled() {
						return this.model.limitOptions.warehouses;
					}),
					minValue: limitMinValue('warehouses'),
				},
			},
			orders: {
				limitOrders: {
					required: requiredIf(function ordersEnabled() { return this.model.limitOptions.orders; }),
					minValue: limitMinValue('orders'),
				},
			},
		},
	};
}

function data() {
	return {
		loading: false,
		roleSearch: '',
		disabledDates: true,
		paymentsForm: [
			{ code: 'annual', name: 'Anual' },
			{ code: 'biannual', name: 'Semestral' },
			{ code: 'quartely', name: 'Trimestral' },
			{ code: 'montly', name: 'Mensual' },
		],
		typePayments: [
			{ code: 'cash', name: 'Contado' },
			{ code: 'deposit', name: 'Depósito' },
			{ code: 'card', name: 'Tarjeta de crédito' },
		],
		model: {
			codePlan: null,
			name: null,
			description: null,
			price: null,
			promotions: { discount: null },
			limitAccess: null,
			users: [],
			subsidiariesLimit: null,
			limitOptions: {
				subsidiaries: false,
				products: false,
				warehouses: false,
				orders: false,
			},
			products: { limitProducts: null },
			electronicBillingEnabled: false,
			electronicBilling: { quantity: 0 },
			electronicBillingMonthly: { quantity: 0 },
			electronicFirm: false,
			notification: 5,
			recalculateKardex: 0,
			paymentFormId: null,
			startDate: null,
			endDate: null,
			typePayment: [],
			warehouses: { limitWarehouses: null },
			orders: { limitOrders: null },
			additionalServices: ADDITIONAL_SERVICES.map(service => ({ ...service, isSelected: false })),
			otherConfigurations: [
				{ code: 'report', name: 'Reportes', description: 'Consulta y generación de reportes del sistema.', icon: 'assessment', isSelected: false },
				{ code: 'support_type', name: 'Soporte técnico', description: 'Atención y acompañamiento técnico para el plan.', icon: 'headset_mic', isSelected: false },
				{ code: 'xml_load', name: 'Carga XML', description: 'Importación de documentos mediante archivos XML.', icon: 'cloud_upload', isSelected: false },
				{ code: 'relative_store', name: 'Tienda relativa', description: 'Asociación y operación con tiendas relacionadas.', icon: 'store', isSelected: false },
				{ code: 'kardex', name: 'Kardex', description: 'Control de movimientos y existencias de inventario.', icon: 'inventory', isSelected: false },
				{ code: 'flag_franchises', name: 'Franquicia', description: 'Habilita la operación por franquicias.', icon: 'store_mall_directory', isSelected: false },
			],
		},
	};
}

export default {
	name: 'plan-form',
	components: { dateComponent },
	computed: {
		kardexEnabled() {
			return isConfigurationSelected(this.model.otherConfigurations, 'kardex');
		},
		configuredProfiles() {
			return this.model.users.filter(user => user.isSelected).length;
		},
		filteredUserProfiles() {
			const search = this.roleSearch.toLowerCase();
			if (!search) return this.model.users;
			return this.model.users.filter(user =>
				user.name.toLowerCase().includes(search) || user.code.toLowerCase().includes(search));
		},
		selectedUserProfiles() {
			return this.model.users.reduce((profiles, user, formIndex) => (
				user.isSelected ? [...profiles, { ...user, formIndex }] : profiles
			), []);
		},
		totalAssignedUsers() {
			return this.model.users
				.filter(user => user.isSelected)
				.reduce((total, user) => total + (Number(user.quantity) || 0), 0);
		},
	},
	created,
	data,
	methods: {
		applyPlanData,
		buildPlanPayload,
		changeUserQuantity,
		clearUserSelection,
		clearDate,
		closeModal,
		getUserInitials,
		loadData,
		loadRoles,
		setDate,
		submitForm,
		toggleLimitOption,
		toggleAdditionalService,
		toggleOtherConfiguration,
		toggleUserProfile,
	},
	validations,
	props: {
		codeProject: { type: String },
		idPlan: { type: [Number, String] },
		codePlan: { type: String },
		users: { type: Array, default: () => [] },
	},
};
</script>

<style lang="scss" scoped>
.plan-form { background: #f4f7f5; height: calc(100vh - 100px); overflow: auto; padding: 18px 20px 88px; }
.plan-form-shell { margin: 0 auto; max-width: 1240px; }
.plan-form-intro { align-items: center; background: linear-gradient(135deg, #1A4338, #285f50); border-radius: 14px; box-shadow: 0 8px 24px rgba(26, 67, 56, .18); color: white; display: flex; gap: 14px; margin-bottom: 14px; padding: 16px 18px; }
.plan-form-intro small { color: #bcd8cf; font-size: 8px; font-weight: 700; letter-spacing: .12em; }
.plan-form-intro h2 { font-size: 18px; line-height: 1.3; margin: 2px 0; }
.plan-form-intro p { color: #d8e8e3; font-size: 11px; margin: 0; }
.plan-form-intro-icon, .plan-section-header > span { align-items: center; background: rgba(255, 255, 255, .13); border: 1px solid rgba(255, 255, 255, .16); border-radius: 10px; display: flex; flex: 0 0 42px; height: 42px; justify-content: center; width: 42px; }
.plan-form-intro-icon .icon { color: white; font-size: 22px; }
.plan-sections { display: grid; gap: 14px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.plan-section { background: white; border: 1px solid #dce7e2; border-radius: 13px; box-shadow: 0 5px 18px rgba(31, 55, 48, .055); padding: 15px; }
.plan-section-wide { grid-column: 1 / -1; }
.plan-section-header { align-items: center; border-bottom: 1px solid #e5ede9; display: flex; gap: 10px; margin-bottom: 14px; padding-bottom: 11px; }
.plan-section-header > span { background: #eaf3ef; border-color: #d9e9e2; flex-basis: 36px; height: 36px; width: 36px; }
.plan-section-header > span .icon { color: #1A4338; font-size: 19px; }
.plan-section-header h3 { color: #29445a; font-size: 13px; margin: 0; }
.plan-section-header p { color: #82928e; font-size: 9px; margin: 2px 0 0; }
.plan-section-switch { align-items: center; display: flex; gap: 6px; margin-left: auto; }
.plan-section-switch strong { background: #e6f2ed; border-radius: 10px; color: #1A694f; font-size: 8px; padding: 4px 8px; text-transform: uppercase; }
.plan-section-switch .input-group { margin: 0; padding: 0; }
.plan-section-disabled .plan-section-switch strong { background: #eef1f0; color: #87928e; }
.plan-feature-disabled { align-items: center; background: #f5f7f6; border: 1px dashed #cbd7d2; border-radius: 10px; display: flex; gap: 10px; justify-content: center; min-height: 96px; padding: 14px; }
.plan-feature-disabled > span { align-items: center; background: #e8edeb; border-radius: 9px; display: flex; flex: 0 0 36px; height: 36px; justify-content: center; width: 36px; }
.plan-feature-disabled .icon { color: #82918c; font-size: 19px; }
.plan-feature-disabled strong { color: #556961; font-size: 10px; }
.plan-feature-disabled p { color: #8a9994; font-size: 8px; margin: 2px 0 0; }
.plan-fields, .plan-date-grid { display: grid; gap: 12px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.plan-fields-general { grid-template-columns: minmax(180px, .65fr) minmax(260px, 1.35fr); }
.plan-fields-kardex { margin-top: 12px; }
.plan-field-full { grid-column: 1 / -1; }
.plan-field { min-width: 0; }
.plan-field-help { color: #82948d; display: block; font-size: 9px; margin: 4px 2px 0; }
.form-group__message { color: #d94d5c; display: block; font-size: 9px; margin: 4px 2px 0; }
.plan-limit-grid { display: grid; gap: 10px; grid-template-columns: repeat(5, minmax(0, 1fr)); }
.plan-limit-card { background: #f8faf9; border: 1px solid #e0e9e5; border-radius: 10px; padding: 11px; }
.plan-limit-card-featured { background: #eff7f3; border-color: #bed8ce; }
.plan-limit-card-disabled { background: #f5f7f6; border-color: #e2e8e5; }
.plan-limit-heading { align-items: center; display: flex; gap: 7px; margin-bottom: 9px; }
.plan-limit-heading .icon { color: #52776b; font-size: 18px; }
.plan-limit-heading > div { display: flex; flex: 1; flex-direction: column; min-width: 0; }
.plan-limit-heading strong { color: #315145; font-size: 10px; }
.plan-limit-heading small { color: #879790; font-size: 8px; line-height: 1.25; }
.plan-limit-toggle { align-items: center; background: #e9f1ed; border: 1px solid #bfd2ca; border-radius: 10px; color: #40685a; cursor: pointer; display: flex; flex: 0 0 auto; font-size: 7px; font-weight: 700; gap: 4px; padding: 4px 7px; text-transform: uppercase; transition: .18s ease; }
.plan-limit-toggle:hover { background: #dcebe5; border-color: #8eb3a4; color: #1A4338; }
.plan-limit-toggle > span { background: #a9b5b0; border-radius: 50%; height: 6px; width: 6px; }
.plan-limit-toggle-active { background: #dcefe7; border-color: #b6d6c9; color: #1A694f; }
.plan-limit-toggle-active > span { background: #279468; box-shadow: 0 0 0 2px rgba(39, 148, 104, .14); }
.plan-limit-enable { align-items: center; background: #fff; border: 1px dashed #9fbbb0; border-radius: 8px; color: #315d4f; cursor: pointer; display: flex; gap: 7px; height: 48px; justify-content: center; padding: 5px 8px; transition: .18s ease; width: 100%; }
.plan-limit-enable:hover { background: #e8f3ee; border-color: #477e6b; box-shadow: 0 3px 9px rgba(26, 67, 56, .09); }
.plan-limit-enable .icon { color: #2f7d62; font-size: 18px; }
.plan-limit-enable > span { display: flex; flex-direction: column; text-align: left; }
.plan-limit-enable strong { color: #315d4f; font-size: 8px; }
.plan-limit-enable small { color: #879790; font-size: 7px; margin-top: 1px; }
.plan-subsection { background: #f6f9f7; border: 1px solid #dce9e3; border-radius: 12px; margin-top: 15px; padding: 14px; }
.plan-roles-heading { align-items: center; display: flex; gap: 16px; justify-content: space-between; margin-bottom: 13px; }
.plan-roles-title { align-items: center; display: flex; gap: 10px; min-width: 0; }
.plan-roles-title > span { align-items: center; background: #dfeee8; border-radius: 9px; display: flex; flex: 0 0 36px; height: 36px; justify-content: center; width: 36px; }
.plan-roles-title .icon { color: #1A4338; font-size: 19px; }
.plan-roles-title h4 { color: #29483e; font-size: 12px; margin: 0; }
.plan-roles-title p { color: #72867f; font-size: 9px; margin: 2px 0 0; }
.plan-roles-summary { align-items: stretch; display: flex; flex: 0 0 auto; gap: 7px; }
.plan-roles-summary > div { align-items: center; background: white; border: 1px solid #d8e5df; border-radius: 9px; display: flex; flex-direction: column; justify-content: center; min-width: 72px; padding: 6px 10px; }
.plan-roles-summary strong { color: #1A4338; font-size: 13px; line-height: 1; }
.plan-roles-summary small { color: #83938d; font-size: 8px; margin-top: 3px; }
.plan-roles-reset { align-items: center; background: transparent; border: 1px solid #cbdcd5; border-radius: 9px; color: #49685e; display: flex; font-size: 9px; font-weight: 600; gap: 3px; padding: 0 10px; transition: .18s ease; }
.plan-roles-reset:hover { background: #e7f1ed; border-color: #9ebcaf; color: #1A4338; }
.plan-roles-reset .icon { color: currentColor; font-size: 14px; }
.plan-role-picker { background: white; border: 1px solid #dbe6e1; border-radius: 11px; overflow: hidden; }
.plan-role-picker-toolbar { align-items: center; background: #eef5f2; border-bottom: 1px solid #dbe6e1; display: flex; gap: 12px; justify-content: space-between; padding: 9px 11px; }
.plan-role-picker-toolbar > span { color: #72877e; flex: 0 0 auto; font-size: 8px; font-weight: 700; text-transform: uppercase; }
.plan-role-search { align-items: center; background: white; border: 1px solid #ccddd6; border-radius: 8px; display: flex; flex: 0 1 390px; height: 36px; overflow: hidden; padding: 0 9px; transition: .18s ease; }
.plan-role-search:focus-within { border-color: #6da18f; box-shadow: 0 0 0 3px rgba(46, 107, 89, .1); }
.plan-role-search > .icon { color: #789087; font-size: 17px; }
.plan-role-search input { background: transparent; border: 0; color: #344f46; flex: 1; font-size: 10px; height: 100%; min-width: 0; outline: 0; padding: 0 7px; }
.plan-role-search button { align-items: center; background: transparent; border: 0; display: flex; justify-content: center; padding: 2px; }
.plan-role-search button .icon { color: #899a94; font-size: 15px; }
.plan-role-options { display: grid; gap: 7px; grid-template-columns: repeat(4, minmax(0, 1fr)); max-height: 210px; overflow: auto; padding: 10px; }
.plan-role-option { align-items: center; background: #f8faf9; border: 1px solid #e0e8e4; border-radius: 8px; color: #385248; display: flex; gap: 7px; min-height: 46px; min-width: 0; padding: 7px 8px; text-align: left; transition: .18s ease; }
.plan-role-option:hover { background: #f0f7f4; border-color: #afc9be; }
.plan-role-option-selected { background: #eaf5f0; border-color: #78aa98; box-shadow: inset 3px 0 #1A4338; }
.plan-role-option-check { align-items: center; background: #e5ede9; border-radius: 7px; display: flex; flex: 0 0 27px; height: 27px; justify-content: center; width: 27px; }
.plan-role-option-check .icon { color: #547267; font-size: 15px; }
.plan-role-option-selected .plan-role-option-check { background: #1A4338; }
.plan-role-option-selected .plan-role-option-check .v-icon,
.plan-role-option-selected .plan-role-option-check .plan-role-selected-icon,
.plan-role-option-selected .plan-role-option-check i { color: #fff !important; }
.plan-role-option > span:nth-child(2) { display: flex; flex: 1; flex-direction: column; min-width: 0; }
.plan-role-option strong { font-size: 9px; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.plan-role-option small { color: #8a9994; font-family: monospace; font-size: 7px; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.plan-role-option em { color: #80928b; flex: 0 0 auto; font-size: 7px; font-style: normal; font-weight: 700; text-transform: uppercase; }
.plan-role-option-selected em { color: #1A694f; }
.plan-role-search-empty { align-items: center; color: #879790; display: flex; font-size: 9px; gap: 6px; justify-content: center; min-height: 76px; }
.plan-role-search-empty .icon { color: #9daca6; font-size: 18px; }
.plan-selected-roles { margin-top: 13px; }
.plan-selected-roles-heading { align-items: center; display: flex; justify-content: space-between; margin-bottom: 8px; }
.plan-selected-roles-heading > div { display: flex; flex-direction: column; }
.plan-selected-roles-heading strong { color: #304e43; font-size: 10px; }
.plan-selected-roles-heading small { color: #84958f; font-size: 8px; margin-top: 2px; }
.plan-selected-roles-heading > span { align-items: center; background: #1A4338; border-radius: 12px; color: white; display: flex; font-size: 8px; font-weight: 700; height: 20px; justify-content: center; min-width: 20px; padding: 0 7px; }
.plan-role-grid { display: grid; gap: 9px; grid-template-columns: repeat(3, minmax(0, 1fr)); }
.plan-role-card { background: white; border: 1px solid #dae5e0; border-radius: 10px; min-width: 0; padding: 10px; transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease; }
.plan-role-card:hover { border-color: #afc9be; box-shadow: 0 4px 12px rgba(26, 67, 56, .07); transform: translateY(-1px); }
.plan-role-card-active { background: #f3faf6; border-color: #80b5a2; box-shadow: inset 3px 0 #1A4338; }
.plan-role-information { align-items: center; display: flex; gap: 8px; margin-bottom: 9px; min-height: 34px; }
.plan-role-avatar { align-items: center; background: #e6f0ec; border-radius: 8px; color: #315d4f; display: flex; flex: 0 0 32px; font-size: 9px; font-weight: 700; height: 32px; justify-content: center; letter-spacing: .04em; width: 32px; }
.plan-role-card-active .plan-role-avatar { background: #1A4338; color: white; }
.plan-role-information > div { display: flex; flex: 1 1 auto; flex-direction: column; min-width: 0; }
.plan-role-information strong { color: #324c5f; font-size: 10px; line-height: 1.25; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.plan-role-information small { color: #8a9a94; font-family: monospace; font-size: 8px; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.plan-role-status { background: #edf1ef; border-radius: 10px; color: #85928d; flex: 0 0 auto; font-size: 7px; font-weight: 700; padding: 3px 6px; text-transform: uppercase; }
.plan-role-card-active .plan-role-status { background: #d8eee4; color: #1A694f; }
.plan-role-counter { align-items: stretch; background: #f2f6f4; border: 1px solid #dce6e2; border-radius: 8px; display: grid; grid-template-columns: 34px minmax(0, 1fr) 34px; min-height: 42px; overflow: hidden; }
.plan-role-counter button { align-items: center; background: #e7efeb; border: 0; color: #315b4e; display: flex; justify-content: center; transition: .18s ease; }
.plan-role-counter button:hover:not(:disabled) { background: #1A4338; color: white; }
.plan-role-counter button:disabled { color: #afbbb7; cursor: not-allowed; }
.plan-role-counter button .icon { color: currentColor; font-size: 16px; }
.plan-role-counter label { align-items: center; background: white; display: flex; flex-direction: column; justify-content: center; min-width: 0; padding: 3px 6px; }
.plan-role-counter label span { color: #87968f; font-size: 7px; line-height: 1; text-transform: uppercase; }
.plan-role-counter input { background: transparent; border: 0; color: #29493f; font-size: 14px; font-weight: 700; height: 22px; outline: 0; text-align: center; width: 100%; }
.plan-role-counter input::-webkit-inner-spin-button, .plan-role-counter input::-webkit-outer-spin-button { appearance: none; margin: 0; }
.plan-roles-empty { align-items: center; background: #eef4f1; border: 1px dashed #b9cec5; border-radius: 10px; display: flex; gap: 10px; justify-content: center; margin-top: 12px; min-height: 74px; padding: 12px; text-align: left; }
.plan-roles-empty > span { align-items: center; background: white; border-radius: 9px; display: flex; flex: 0 0 34px; height: 34px; justify-content: center; width: 34px; }
.plan-roles-empty .icon { color: #58776b; font-size: 18px; }
.plan-roles-empty strong { color: #405c52; font-size: 10px; }
.plan-roles-empty p { color: #84958f; font-size: 8px; margin: 2px 0 0; }
.plan-toggle { align-items: center; background: #f6f9f8; border: 1px solid #e0e9e5; border-radius: 9px; display: flex; justify-content: space-between; min-height: 55px; padding: 8px 12px; }
.plan-toggle div { display: flex; flex-direction: column; }
.plan-toggle strong { color: #345246; font-size: 10px; }
.plan-toggle small { color: #84958f; font-size: 8px; }
.plan-services-grid { display: grid; gap: 8px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.plan-service-card { align-items: center; background: #f7f9f8; border: 1px solid #dfe7e3; border-radius: 10px; color: #375248; display: flex; gap: 9px; min-height: 64px; padding: 9px; text-align: left; transition: .18s ease; width: 100%; }
.plan-service-card:hover { background: #f0f7f4; border-color: #a9c4b9; transform: translateY(-1px); }
.plan-service-card-active { background: #eaf5f0; border-color: #72a792; box-shadow: inset 3px 0 #1A4338; }
.plan-service-icon { align-items: center; background: #e7efeb; border-radius: 9px; display: flex; flex: 0 0 34px; height: 34px; justify-content: center; width: 34px; }
.plan-service-icon .icon { color: #547267; font-size: 18px; }
.plan-service-card-active .plan-service-icon { background: #1A4338; }
.plan-service-card-active .plan-service-icon .icon,
.plan-service-card-active .plan-service-icon .v-icon,
.plan-service-card-active .plan-service-icon i { color: #fff !important; }
.plan-service-copy { display: flex; flex: 1; flex-direction: column; min-width: 0; }
.plan-service-copy strong { color: #315145; font-size: 10px; }
.plan-service-copy small { color: #82928c; font-size: 8px; line-height: 1.35; margin-top: 2px; }
.plan-service-action { align-items: center; background: #e6ece9; border-radius: 50%; display: flex; flex: 0 0 24px; height: 24px; justify-content: center; width: 24px; }
.plan-service-action .icon { color: #70847c; font-size: 15px; }
.plan-service-card-active .plan-service-action { background: #1A4338; }
.plan-service-card-active .plan-service-action .icon,
.plan-service-card-active .plan-service-action .v-icon,
.plan-service-card-active .plan-service-action i { color: #fff !important; }
.plan-date-grid label { color: #405b50; display: block; font-size: 10px; font-weight: 600; margin-bottom: 7px; }
@media (max-width: 1050px) { .plan-limit-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } .plan-role-options { grid-template-columns: repeat(3, minmax(0, 1fr)); } .plan-role-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 760px) { .plan-form { height: calc(100vh - 92px); padding: 12px 12px 82px; } .plan-sections { grid-template-columns: 1fr; } .plan-section-wide { grid-column: auto; } .plan-fields, .plan-fields-general, .plan-date-grid { grid-template-columns: 1fr; } .plan-field-full { grid-column: auto; } .plan-limit-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .plan-roles-heading { align-items: flex-start; flex-direction: column; } .plan-roles-summary { width: 100%; } .plan-roles-summary > div { flex: 1; } }
@media (max-width: 600px) { .plan-role-options { grid-template-columns: repeat(2, minmax(0, 1fr)); } .plan-role-picker-toolbar { align-items: stretch; flex-direction: column; } .plan-role-search { flex-basis: 36px; } }
@media (max-width: 460px) { .plan-limit-grid, .plan-role-grid, .plan-role-options, .plan-services-grid { grid-template-columns: 1fr; } .plan-form-intro { align-items: flex-start; } .plan-roles-summary { flex-wrap: wrap; } .plan-roles-reset { min-height: 32px; width: 100%; justify-content: center; } }
</style>

<style lang="scss">
.plan-form {
	.v-text-field, .v-textarea, .v-select { margin-top: 0; padding-top: 0; }
	.v-input__slot, .input-group__input { border-color: #d5e2dc !important; border-radius: 8px !important; box-shadow: none !important; }
	.v-input--is-focused .v-input__slot, .input-group--focused .input-group__input { border-color: #2E6B59 !important; box-shadow: 0 0 0 3px rgba(46, 107, 89, .12) !important; }
	.v-text-field--box .v-input__slot { min-height: 48px; }
	.v-textarea.v-text-field--box .v-input__slot { min-height: 82px; }
	.v-chip { background: #e5f1ec; color: #1A4338; font-size: 10px; }
}
</style>



// WEBPACK FOOTER //
// src/components/plans/plan-form.vue